import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import Header from "../layout/header";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { createTimeline, getAlerts, getDashboardService } from "../../api/dashboard";
import PortfolioAllocationChart from "./PortfolioAllocationChart";
import moment from "moment";
import { useDocumentTitle } from "../../utils/Common";
import { getUserServices } from "../liveCalls/LiveCalls.service";
import { useTheme } from "../../utils/theme";

const OFFER_ENDS_AT = moment("2026-05-30 23:59:59", "YYYY-MM-DD HH:mm:ss").valueOf();

const getOfferCountdown = (endsAt) => {
    const remainingMs = Math.max(moment(endsAt).diff(moment()), 0);
    const totalSeconds = Math.floor(remainingMs / 1000);

    return {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        isExpired: remainingMs === 0,
    };
};

export default function Dashboard2() {
    const { theme } = useTheme();
    const userDetail = useSelector((state) => state.auth.userDetail);
    const navigate = useNavigate();
    const [DashboardData, setDashboardData] = useState(null);
    const [AlertData, setAlertData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const allocationData = [
        { label: "Equity", value: 25000000 },
        { label: "ETFs", value: 24998976 },
        { label: "Multibaggers", value: 24999975 },
        { label: "Mutual Funds", value: 24999897 },
        { label: "Opportunities", value: 24999485 },
    ];
    const [hasActiveServices, setHasActiveServices] = useState([]);
    const [expiredServices, setExpiredServices] = useState([]);
    const [expiringSoonServices, setExpiringSoonServices] = useState([]);
    const [showExpiryDetails, setShowExpiryDetails] = useState(false);
    const [isRenewRequestLoading, setIsRenewRequestLoading] = useState(false);
    const [offerCountdown, setOfferCountdown] = useState(() =>
        getOfferCountdown(OFFER_ENDS_AT),
    );
    const primaryButtonStyle = {
    backgroundColor: "var(--bs-emphasis-color)",
    borderColor: "var(--bs-emphasis-color)",
    color: "var(--bs-body-bg)",
  };

    useDocumentTitle("Dashboard")
    useEffect(() => {
        const updateOfferCountdown = () => {
            setOfferCountdown(getOfferCountdown(OFFER_ENDS_AT));
        };

        updateOfferCountdown();
        const timerId = window.setInterval(updateOfferCountdown, 1000);

        return () => window.clearInterval(timerId);
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                const userServiceData = await getUserServices();
                const response = await getDashboardService();
                const alerts = await getAlerts();
                const services = userServiceData?.data?.data.filter(
                    (item) => item.status === "active"
                ) || [];
                // 🔥 Filter logic using dueDaysOfExpiry
                const expired = services.filter(
                    (s) => s.dueDaysOfExpiry <= 0
                );
                setHasActiveServices(services)

                const expiringSoon = services.filter(
                    (s) => s.dueDaysOfExpiry > 0 && s.dueDaysOfExpiry < 10
                );


                // ✅ Set state
                setExpiredServices(expired);
                setExpiringSoonServices(expiringSoon);

                // ✅ Optional: auto open expiry details
                // if (expired.length > 0 || expiringSoon.length > 0) {
                //     setShowExpiryDetails(true);
                // }
                setDashboardData(response.data);
                setAlertData(alerts.data);
            } catch (error) {
                console.error("Error while fetching the dashboard details ", error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, []);

    const handleRedirect = (id, index, link1, link2) => {
        localStorage.setItem("navigateId", id);

        navigate(DashboardData?.products[index]?.isSubscribed ? link1 : link2);
    };

    const handlePortfolioRedirect = (item) => {
        localStorage.setItem("selectedPortfolio", item.invPortId);

        if (item.invType !== "equity" && item.invType !== "etf") {
            const pacId = item.pacId || item.pacid || item.pac_id;

            if (pacId) {
                localStorage.setItem("pacId", pacId);
            }
        }
    };

    const formatAmount = (value) =>
        Number(value || 0).toLocaleString("en-IN", {
            maximumFractionDigits: 2,
        });

    const getServiceName = (service) =>
        service?.pacName || service?.serviceName || "Your service";

    const getExpiryMessage = (service, isExpired) => {
        const days = Math.abs(Number(service?.dueDaysOfExpiry) || 0);
        const dayText = days === 1 ? "day" : "days";
        const serviceName = getServiceName(service);

        if (isExpired) {
            return days === 0
                ? `${serviceName} service expired today.`
                : `${serviceName} service expired ${days} ${dayText} ago.`;
        }

        return `${serviceName} service will expire in ${days} ${dayText}.`;
    };

    const handleContactSales = async () => {
        const renewalServices = [...expiredServices];
        const serviceNames = [
            ...new Set(renewalServices.map((service) => getServiceName(service))),
        ].join(", ");

        if (renewalServices.length === 0 || isRenewRequestLoading) {
            return;
        }

        setIsRenewRequestLoading(true);

        try {
            await createTimeline({
                type: "renewRequest",
                message: `${serviceNames} renewal request created by ${userDetail?.firstName || "User"}`,
            });

            toast.success("Renewal request sent to sales");
        } catch (error) {
            console.error("Error while creating renewal request", error);
            toast.error(error?.response?.data?.message || "Unable to send renewal request");
        } finally {
            setIsRenewRequestLoading(false);
        }
    };

    const optionServiceNames = [
        "EP_INDEX_OPTIONS",
        "EP_STOCK_OPTION",
        "EP_PREMIUM_INDEX_OPTIONS",
    ];
    const optionServices =
        DashboardData?.myServices?.filter((item) =>
            optionServiceNames.includes(item.pacFirebaseName),
        ) || [];
    const optionMessagesCount = optionServices.reduce(
        (sum, item) => sum + (item.mainMessagesCount || 0),
        0,
    );

    return (
        <Layout>
            {/* <Header/> */}
            {isLoading ? (
                <div class="d-flex justify-content-center align-items-center vh-100">
                    <div class="text-center">
                        <div class="spinner-border text-primary mb-2" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="fw-semibold">Loading, please wait...</div>
                    </div>
                </div> // or your skeleton loader
            ) : (
                <>
                    <section id="create-portfolio" class="d-none py-4 text-body-emphasis">
                        <div class="container">
                            <div class="row d-none align-items-center align-items-md-start g-3">
                                <div class="col-md-6">
                                    <div class="d-flex align-items-baseline gap-2">
                                        <h4 class="fw-bold">Hello {userDetail.firstName},</h4>
                                        <small class="fw-normal text-muted"></small>
                                    </div>
                                    <p class="mb-0">
                                        Welcome to unified platform for 360° stock market investing
                                        across financial products.
                                    </p>
                                </div>
                                <div class="col-md-6">
                                    <div class=" text-white bg-black p-3 rounded-3 bg-gradient">
                                        <h4 class="fw-bold fs-3 mb-0 fst-italic">
                                            <span class="text-primary">WealthX</span> A true way to
                                            create wealth
                                        </h4>
                                        <div class="d-flex align-items-center justify-content-between gap-2">
                                            <div class="">
                                                <a
                                                    href=""
                                                    class="text-decoration-none text-white pe-auto"
                                                >
                                                    <small class="fw-normal fst-italic">
                                                        Unlock all features in one subscription.
                                                        <span class="text-decoration-line-through">
                                                            ₹1,20,000
                                                        </span>
                                                        <span class="fs-5">₹75,000</span>
                                                    </small>
                                                    <i class="fa fa-arrow-right text-white"></i>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-8">
                                    <div class="row g-3 align-items-center">
                                        <div class="col-md-6">
                                            <small class="text-muted d-block">
                                                Enter Funds available for investment
                                            </small>
                                            <div class="input-group mt-2 mb-0 mb-md-4">
                                                <input
                                                    type="number"
                                                    class="form-control fw-bold "
                                                    id="floatingInput"
                                                    placeholder=""
                                                    readOnly={!DashboardData}
                                                    style={{ fontSize: "1.2rem", letterSpacing: "1px" }}
                                                />
                                                <button
                                                    class="btn btn-dark"
                                                    disabled={!DashboardData}
                                                    type="button"
                                                    id="inputGroupFileAddon04"
                                                >
                                                    Continue
                                                </button>
                                            </div>
                                        </div>
                                        <div class="col-md-4"></div>
                                        <div class="col-md-4 col-6">
                                            <small class="text-muted d-block">
                                                Risk Profile{" "}
                                                <a
                                                    href="#"
                                                    class="text-secondary"
                                                    title="A stock market risk profile is an assessment of an investor's willingness and ability to tolerate potential losses for potential gains, determined by factors like age, income, financial goals, and experience, to guide suitable investment choices like stocks versus bonds, aligning portfolio risk with personal comfort levels."
                                                >
                                                    <i class="fa fa-info-circle"></i>
                                                </a>
                                            </small>
                                            <p
                                                class={`"mb-0 fs-5 ${userDetail.rpfProfileType == "Aggressive" ? "text-success" : userDetail.rpfProfileType == "Moderate" ? "text-warning" : "text-danger"}`}
                                            >
                                                {userDetail.rpfProfileType
                                                    ? userDetail.rpfProfileType
                                                    : "-"}
                                            </p>
                                        </div>
                                        <div class="col-md-4 col-6">
                                            <small class="text-muted d-block">
                                                KYC{" "}
                                                <a
                                                    href="#"
                                                    class="text-secondary"
                                                    title="As per SEBI regulation KYC is mandatory."
                                                >
                                                    <i class="fa fa-info-circle"></i>
                                                </a>
                                            </small>
                                            <p
                                                class={`mb-0 fs-5 ${userDetail.kycObj ? "text-success" : "text-danger"}`}
                                            >
                                                {userDetail.kycObj ? "Completed" : "Pending"}
                                            </p>
                                        </div>
                                        <div class="col-md-4 col-12">
                                            <hr class="d-block d-md-none" />
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <small class="d-block text-start text-md-center text-capitalize">
                                        Fund allocation across multiple financial products
                                    </small>
                                    {/* <div class="d-flex align-items-center justify-content-center">
                                <?php
                                // ---------- PHP DATA SETUP ----------
                                $stocks = ['Equity', 'ETFs', 'Multibaggers', 'Mutual Funds',  'Opportunities'];

                                $palette = [
                                '#4285F4', // Google Blue
                                '#DB4437', // Google Red
                                '#F4B400', // Yellow
                                '#0F9D58', // Green
                                '#AB47BC', // Purple
                                ];



                                function formatINR($num)
                                {
                            return number_format($num, 2, '.', ',');
                        }

                                $rows = [];

                                for ($i = 0; $i < count($stocks); $i++) {
                                    $symbol = $stocks[$i];
                                $ltp = rand(500, 3500);
                                $avgPrice = $ltp + rand(-50, 50);
                                // $qty      = rand(5, 50);
                                $recomQty = (int) round(25000000 / $ltp);

                                $invested     = $avgPrice * $recomQty;
                                $currentValue = $ltp * $recomQty;
                                $pnl          = $currentValue - $invested;

                            $isProfit = ($pnl >= 0);
                                $pnlIcon  = $isProfit ? "<i class='fa-solid fa-caret-up'></i>" : "<i class='fa-solid fa-caret-down'></i>";
                                $pnlClass = $isProfit ? "text-success" : "text-danger";

                                // pick a color from the palette (wrap around)
                                $color = $palette[$i % count($palette)];

                                $rows[] = [
                                'symbol'        => $symbol,
                                'avgPrice'      => $avgPrice,
                                'recomQty'      => $recomQty,
                                'ltp'           => $ltp,
                                // 'qty'           => $qty,
                                'invested'      => $invested,
                                'currentValue'  => $currentValue,
                                'pnl'           => $pnl,
                                'pnlIcon'       => $pnlIcon,
                                'pnlClass'      => $pnlClass,
                                'color'         => $color
                                ];
                        }

                                // Data for pie chart
                                $chartLabels = array_column($rows, 'symbol');
                                $chartValues = array_map(function ($row) {
                            return round($row['currentValue'], 2);
                        }, $rows);

                                // colors for chart (same order as rows)
                                $chartColors = array_column($rows, 'color');
                        ?>

                                <div class="d-flex justify-content-center align-items-cente mt-4 me-4">
                                    <canvas id="portfolioPieChart"></canvas>
                                </div>
                                <div>
                                    <div id="portfolioPieLegend" class="pie-legend mt-4"></div>
                                </div>
                                <script>
                                    const labels = <?= json_encode($chartLabels) ?>;
                                    const values = <?= json_encode($chartValues) ?>;
                            const numericValues = values.map(v => Number(v) || 0);

                                    // HTML legend plugin
                                    const htmlLegendPlugin = {
                                        id: 'htmlLegend',
                                    afterUpdate(chart, args, options) {
                                    const container = document.getElementById(options.containerID);
                                    if (!container) return;

                                    let list = container.querySelector('ul');
                                    if (!list) {
                                        list = document.createElement('ul');
                                    container.appendChild(list);
                                    }

                                    // Clear old items
                                    list.innerHTML = '';

                                    const {
                                        data
                                    } = chart;
                                    const dataset = data.datasets[0];

                                    const totalValue = dataset.data.reduce((sum, v) => sum + (Number(v) || 0), 0);

                                    const items = data.labels.map((label, i) => {
                                        const value = Number(dataset.data[i]) || 0;
                                    const percentage = totalValue ?
                                    ((value / totalValue) * 100).toFixed(1) + '%' :
                                    '0%';

                                    return {
                                        text: `${label} (${percentage})`,
                                    fillStyle: dataset.backgroundColor[i % dataset.backgroundColor.length],
                                    index: i
                                        };
                                    });

                                    items.forEach(item => {
                                        const li = document.createElement('li');
                                        li.addEventListener('click', () => {
                                        chart.toggleDataVisibility(item.index);
                                    chart.update();
                                        });

                                    const box = document.createElement('span');
                                    box.classList.add('color-box');
                                    box.style.background = item.fillStyle;

                                    const text = document.createElement('span');
                                    text.appendChild(document.createTextNode(item.text));

                                    li.appendChild(box);
                                    li.appendChild(text);
                                    list.appendChild(li);
                                    });

                                    // Enable scroll only if more than 10 items
                                    if (items.length > 10) {
                                        container.classList.add('scroll');
                                    } else {
                                        container.classList.remove('scroll');
                                    }
                                }
                            };

                                    const ctx = document.getElementById('portfolioPieChart');

                                    new Chart(ctx, {
                                        type: 'doughnut',
                                    data: {
                                        labels,
                                        datasets: [{
                                        data: numericValues,
                                    borderWidth: 2,
                                    borderColor: '#ffffff',
                                    hoverOffset: 14,
                                    backgroundColor: [
                                    '#ffc107cc', '#198754cc', '#0dcaf0cc', '#dc3545cc', '#6c757dcc',
                                    '#edc949', '#af7aa1', '#ff9da7', '#9c755f', '#bab0ab', '#534741ff'
                                    ]
                                    }]
                                },
                                    options: {
                                        responsive: true,
                                    maintainAspectRatio: false,
                                    layout: {
                                        padding: 0
                                    },
                                    cutout: '65%',
                                    plugins: {
                                        legend: {
                                        display: false
                                        },
                                    htmlLegend: {
                                        containerID: 'portfolioPieLegend'
                                        },
                                    tooltip: {
                                        padding: 5,
                                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                    titleFont: {
                                        size: 10,
                                    weight: '600'
                                            },
                                    bodyFont: {
                                        size: 10
                                            },
                                    callbacks: {
                                        label: (ctx) => {
                                                    const label = ctx.label || '';
                                    const value = Number(ctx.raw) || 0;
                                    const dataset = ctx.chart.data.datasets[0];
                                                    const total = dataset.data.reduce((sum, v) => sum + (Number(v) || 0), 0);
                                    const percentage = total ?
                                    ((value / total) * 100).toFixed(1) :
                                    0;

                                    return `${label}: ₹${value.toLocaleString()} (${percentage}%)`;
                                                }
                                            }
                                        }
                                    },
                                    animation: {
                                        animateScale: true,
                                    animateRotate: true,
                                    duration: 900,
                                    easing: 'easeOutCubic'
                                    }
                                },
                                    plugins: [htmlLegendPlugin]
                            });
                                </script>
                            </div> */}
                                </div>
                            </div>
                            <div class="row g-3">
                                <div class="col-lg-8 col-md-7 col-12">
                                    <div class="d-flex align-items-baseline gap-2">
                                        <h4 class="fw-bold">Hello {userDetail.firstName},</h4>
                                        <small class="fw-normal text-muted"></small>
                                    </div>
                                    <p className="mb-2 dashboard-hero-subtitle">
                                        Welcome to unified platform for 360° stock market investing
                                        across financial products.
                                    </p>
                                    <div
                                        class="p-3 rounded-3 "
                                        // data-bs-theme="dark"
                                        style={{ backgroundColor: "#0A0A0B" }}
                                    >
                                        <small>
                                            {/* <small class="text-muted mb-3 d-block">
                                                Last Updated:{" "}
                                                <span class="text-white">30 Dec 2025, 3:13 PM</span>
                                            </small> */}
                                            <small
                                                className={`mb-3 d-block ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                    }`}
                                            >
                                                Last Updated:{" "}
                                                <span className={theme === "dark" ? "text-white" : "text-body-emphasis"}>
                                                    30 Dec 2025, 3:13 PM
                                                </span>
                                            </small>
                                        </small>
                                        <div class="row gy-3 justify-content-between">
                                            <div class="col-md col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total Client Assets Under Advisory at EquityPandit"
                                                    >
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            AUA with EP{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 fs-5 text-body-emphasis">
                                                        12,00,000.85
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md-auto col-6">
                                                <div class="text-end text-md-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total Client Assets Under Advisory at EquityPandit"
                                                    >
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            Current{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 fs-5 text-body-emphasis">
                                                        12,00,000.65
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md-auto col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total unrealized profit &amp; loss."
                                                    >
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            Current P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 text-success-emphasis fs-5">
                                                        +4,44,000.45
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md-auto col-6">
                                                <div class="text-end text-md-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total unrealized profit &amp; loss."
                                                    >
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            Booked P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 text-success-emphasis fs-5">
                                                        +4,44,000.35
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md-auto">
                                                <hr class="mb-2 mt-0 d-block d-md-none text-white" />
                                                <div class="d-flex justify-content-between align-items-center d-md-inline-block text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total unrealized profit &amp; loss."
                                                    >
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            Total P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 text-success-emphasis fs-5">
                                                        +4,44,000.25
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row g-3 mt-0">
                                        <div class="col-md-12">
                                            <h6 class="fw-bold mb-0 mt-2">
                                                <i class="fa fa-bell" aria-hidden="true"></i> Recent
                                                Alerts
                                            </h6>
                                            {/* {DashboardData?.alerts?.map((item,idx)=> (
                                            <a href="/portfolio" class="py-2 px-3 rounded-2 bg-warning bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark">
                                            <div class="d-flex gap-2 align-items-center">
                                                <small><small class="bg-warning rounded-2 text-white px-2 py-0 me-2 py-1">Equity</small></small>
                                                <small> Book full profit in <span style={{"filter": "blur(4px)"}}>xxxx</span> CMP <span style={{"filter": "blur(4px)"}}>xxxx</span></small> <i class="ms-3 fa fa-chevron-right fa-xs" aria-hidden="true"></i>
                                            </div>
                                        </a>
                                        ))} */}

                                            <a
                                                href="/multibagger"
                                                class="py-2 px-3 rounded-2 bg-primary bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-primary rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            Multibaggers
                                                        </small>
                                                    </small>
                                                    <small> New recommendation </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                            <a
                                                href="/etf"
                                                class="py-2 px-3 rounded-2 bg-danger bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-danger rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            ETFs
                                                        </small>
                                                    </small>
                                                    <small>
                                                        {" "}
                                                        Book full profit in{" "}
                                                        <span style={{ filter: "blur(4px)" }}>
                                                            xxxx
                                                        </span>{" "}
                                                        CMP{" "}
                                                        <span style={{ filter: "blur(4px)" }}>xxxx</span>
                                                    </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                            <a
                                                href="/ipo"
                                                class="py-2 px-3 rounded-2 border mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-secondary rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            IPO
                                                        </small>
                                                    </small>
                                                    <small>
                                                        {" "}
                                                        Kindly Avoid to Subscribe Apollo Techno Industries
                                                        Ltd. IPO{" "}
                                                    </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-lg-4 col-md-5 col-12">
                                    <div class="d-flex">
                                        {/* <!-- <i class="fa fa-chart-pie mt-1"></i> --> */}
                                        <div class="">
                                            <h6 class="fw-bold mb-1 mt-0 mt-3 mt-md-0">
                                                Your Risk Profile:{" "}
                                                <span class="text-success fw-normal">
                                                    {userDetail.rpfProfileType}
                                                </span>
                                            </h6>
                                            <small class="d-block text-capitalize mb-2">
                                                Suggested Fund allocation across multiple financial
                                                products.
                                            </small>
                                        </div>
                                    </div>
                                    <div class="border rounded-3 p-3">
                                        {/* <div class="d-flex justify-content-center gap-4 align-items-center">
                                        <canvas id="portfolioPieChart" style="display: block; box-sizing: border-box; height: 140px; width: 140px;" width="140" height="140"></canvas>
                                        <div id="portfolioPieLegend" class="pie-legend mt-2"><ul><li><span class="color-box" style="background: rgba(255, 193, 7, 0.8);"></span><span>Equity (20.0%)</span></li><li><span class="color-box" style="background: rgba(25, 135, 84, 0.8);"></span><span>ETFs (20.0%)</span></li><li><span class="color-box" style="background: rgba(13, 202, 240, 0.8);"></span><span>Multibaggers (20.0%)</span></li><li><span class="color-box" style="background: rgba(220, 53, 69, 0.8);"></span><span>Mutual Funds (20.0%)</span></li><li><span class="color-box" style="background: rgba(108, 117, 125, 0.8);"></span><span>Opportunities (20.0%)</span></li></ul></div>
                                    </div> */}
                                    </div>
                                </div>
                            </div>

                            <div class="row g-3 mt-3">
                                <div class="col-md-12">
                                    <h6 class="fw-bold mb-0">
                                        <i class="fa fa-chart-pie"></i> Fund Allocation
                                    </h6>
                                </div>

                                <div class="col-md-4">
                                    <div class="bg-warning bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                        <a
                                            href={
                                                DashboardData?.products[0]?.isSubscribed
                                                    ? "/portfolio"
                                                    : "/portfolio-lock"
                                            }
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div class="me-3">
                                                    <h1 class="fw-bold fs-4 mb-1">Equity</h1>
                                                    <small class="text-black">
                                                        A carefully curated selection of high-potential
                                                        stocks across sectors
                                                    </small>
                                                </div>
                                                <i
                                                    class="fa fa-arrow-right mt-2"
                                                    aria-hidden="true"
                                                ></i>
                                            </div>
                                        </a>
                                        <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                            <div class="">
                                                <small class="text-muted d-block">Invested</small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[0]?.invested}
                                                </h5>
                                            </div>
                                            <div class="text-end">
                                                <small class="text-muted d-block">
                                                    Current P&amp;L
                                                </small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[0]?.currentVal}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="bg-primary bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                        <a
                                            href={
                                                DashboardData?.products[2]?.isSubscribed
                                                    ? "/multibagger"
                                                    : "/multibagger-lock"
                                            }
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div class="me-3">
                                                    <h1 class="fw-bold fs-4 mb-1">Multibaggers</h1>
                                                    <small class="text-black">
                                                        Fundamentally strong stocks with long-term growth
                                                        potential.
                                                    </small>
                                                </div>
                                                <i
                                                    class="fa fa-arrow-right mt-2"
                                                    aria-hidden="true"
                                                ></i>
                                            </div>
                                        </a>
                                        <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                            <div class="">
                                                <small class="text-muted d-block">Invested</small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[2]?.invested}
                                                </h5>
                                            </div>
                                            <div class="text-end">
                                                <small class="text-muted d-block">
                                                    Current P&amp;L
                                                </small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[2]?.currentVal}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="bg-danger bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                        <div
                                            onClick={() => {
                                                handleRedirect(
                                                    DashboardData?.products[1]?.invPortId,
                                                    1,
                                                    "/etf",
                                                    "/etf-lock",
                                                );
                                            }}
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-start">
                                                <div class="me-3">
                                                    <h1 class="fw-bold fs-4 mb-1">ETFS</h1>
                                                    <small class="text-black">
                                                        ETFs with low cost, high liquidity and a clear
                                                        long-term strategy.
                                                    </small>
                                                </div>
                                                <i
                                                    class="fa fa-arrow-right mt-2"
                                                    aria-hidden="true"
                                                ></i>
                                            </div>
                                        </div>
                                        <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                            <div class="">
                                                <small class="text-muted d-block">Invested</small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[1]?.invested}
                                                </h5>
                                            </div>
                                            <div class="text-end">
                                                <small class="text-muted d-block">
                                                    Current P&amp;L
                                                </small>
                                                <h5 class="mb-0">
                                                    {DashboardData?.products[1]?.currentVal}
                                                </h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* <div class="col-md-3">
                                <div class="bg-danger bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                    <a href={!DashboardData? "/modalportfolio": "/mutual-funds"} class="text-body-emphasis text-decoration-none">
                                        <div class="d-flex justify-content-between align-items-center">
                                            <div>
                                                <h1 class="fw-bold fs-4 mb-1">Mutual Funds</h1>
                                            </div>
                                            <i class="fa fa-arrow-right"></i>
                                        </div>
                                        <small class="text-muted">(5 Mutual Funds Available For SIP)</small>
                                    </a>
                                    <div class="d-flex justify-content-between align-items-center mt-md-5 mt-3">
                                        <div class="">
                                            <small class="text-muted d-block">Monthly SIP
                                                <a href="#" class="text-secondary" title="Based on your monthly income and expenses">
                                                    <i class="fa fa-info-circle"></i>
                                                </a>
                                            </small>
                                            <h4 class="mb-0">7,500</h4>
                                        </div>
                                    </div>
                                </div>
                            </div> */}

                                <div class="col-md-12 pt-3">
                                    <h6 class="fw-bold mb-0">
                                        <i class="fa fa-lightbulb"></i> Opportunities
                                    </h6>
                                </div>

                                <div class="col-md-3">
                                    <div class="border p-3 rounded-3 h-100 position-relative">
                                        <a
                                            href="/ipo"
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 class="fw-bold mb-1">IPO Recommendations</h6>
                                                </div>
                                                <i class="fa fa-arrow-right"></i>
                                            </div>
                                            <small class="text-muted">
                                                Get updates on the latest IPOs
                                            </small>
                                        </a>
                                        <span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                            1<span class="visually-hidden">New</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="border p-3 rounded-3 h-100">
                                        <a
                                            href="/short-term-investments"
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 class="fw-bold mb-1">Short-term Investments</h6>
                                                </div>
                                                <i class="fa fa-arrow-right"></i>
                                            </div>
                                            <small class="text-muted">Duration 15 - 45 days</small>
                                        </a>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="border p-3 rounded-3 h-100 position-relative">
                                        <a
                                            href="/option-hedging"
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 class="fw-bold mb-1">Option Hedging</h6>
                                                </div>
                                                <i class="fa fa-arrow-right"></i>
                                            </div>
                                            <small class="text-muted">Duration 15 - 30 days</small>
                                        </a>
                                        <span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                            1<span class="visually-hidden">New</span>
                                        </span>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="border p-3 rounded-3 h-100 position-relative">
                                        <a
                                            href="/options"
                                            class="text-body-emphasis text-decoration-none"
                                        >
                                            <div class="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 class="fw-bold mb-1">Index / Stock Options</h6>
                                                </div>
                                                <i class="fa fa-arrow-right"></i>
                                            </div>
                                            <small class="text-muted">Duration upto 10 days</small>
                                        </a>
                                        <span class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                            1<span class="visually-hidden">New</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    {(!offerCountdown.isExpired && DashboardData?.isWealthOfferEnable) && (
                        <>
                            {/* {(DashboardData?.isWealthOfferEnable) && ( */}
                            <div className="container-fluid dashboard-offer-strip" role="status" aria-live="polite">
                                <div className="container dashboard-offer-inner">
                                    <div className="dashboard-offer-copy">
                                        <span className="dashboard-offer-eyebrow">
                                            Special Offer Only For You {DashboardData?.isWealthOfferEnable}
                                        </span>
                                        <span className="dashboard-offer-title">
                                            WealthX 50% OFF
                                        </span>
                                    </div>

                                    <div
                                        className="dashboard-offer-timer"
                                        aria-label="Offer countdown"
                                    >
                                        <span className="dashboard-offer-timer-label">
                                            Ends in
                                        </span>
                                        {[
                                            { label: "Days", value: offerCountdown.days },
                                            { label: "Hours", value: offerCountdown.hours },
                                            { label: "Mins", value: offerCountdown.minutes },
                                            { label: "Secs", value: offerCountdown.seconds },
                                        ].map((item) => (
                                            <span className="dashboard-offer-timebox" key={item.label}>
                                                <span className="dashboard-offer-timevalue">
                                                    {String(item.value).padStart(2, "0")}
                                                </span>
                                                <span className="dashboard-offer-timelabel">
                                                    {item.label}
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    <section id="create-portfolio" class="py-3 text-body-emphasis dashboard-home">
                        <div class="container dashboard-home-container">
                            <div class="row g-3 dashboard-home-row">
                                <div class="col-lg-8 order-0 order-lg-0 dashboard-main-column">
                                    <div className="dashboard-hero-copy">
                                        <h4 className="fw-bold mb-2">Hello {userDetail?.firstName},</h4>
                                    </div>
                                    <small className={`mb-2 ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                        }`}>Welcome to your all-in-one destination for 360° stock market and financial product investing.</small>
                                    <p className="d-none">
                                        Welcome to unified platform for 360° stock market investing
                                        across financial products.
                                    </p>
                                    <div
                                        class="p-3 rounded-2 bg-black dashboard-stats-panel dashboard-hero-card mt-2"
                                        data-bs-theme="dark"
                                        style={{ backgroundColor: "#0A0A0B" }}
                                    >
                                        {/* <small class="text-muted mb-3 d-block dashboard-hero-updated">
                                            Last Updated:{" "}
                                            <span class="text-white">
                                                {moment().format("DD MMM YYYY, hh:mm A")}
                                            </span>
                                        </small> */}
                                        <small
                                            className={`mb-3 d-block dashboard-hero-updated ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                }`}
                                        >
                                            Last Updated:{" "}
                                            <span className={`${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                }`}>
                                                {moment().format("DD MMM YYYY, hh:mm A")}
                                            </span>
                                        </small>
                                        <div class="row gy-3 gx-2 dashboard-hero-metrics">
                                            <div class="col-md col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Total client assets under advisory with EquityPandit."
                                                    >
                                                        <small>
                                                            AUA with EP{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 fs-4 text-white dashboard-hero-value">
                                                        {formatAmount(DashboardData?.invested)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Current market value of your portfolio."
                                                    >
                                                        <small>
                                                            Current{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p class="mb-0 fs-4 text-white dashboard-hero-value">
                                                        {formatAmount(DashboardData?.currentVal)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Unrealized profit or loss based on current market value."
                                                    >
                                                        <small>
                                                            Current P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p
                                                        className="mb-0 fs-4 dashboard-hero-value"
                                                        style={{
                                                            color:
                                                                DashboardData?.currentPnlAmt > 0
                                                                    ? "#0AD274"
                                                                    : "#FF4D4F" // choose your red shade
                                                        }}
                                                    // class={`${DashboardData?.currentPnlAmt > 0 ? "text-success-emphasis" : "text-danger-emphasis"} mb-0 fs-4 dashboard-hero-value`}
                                                    >
                                                        {formatAmount(DashboardData?.currentPnlAmt)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="col-md col-6">
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Realized profit or loss from closed positions."
                                                    >
                                                        <small>
                                                            Booked P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p
                                                        className="mb-0 fs-4 dashboard-hero-value"
                                                        style={{
                                                            color:
                                                                DashboardData?.bookedPnlAmt > 0
                                                                    ? "#0AD274"
                                                                    : "#FF4D4F" // choose your red shade
                                                        }}
                                                    >
                                                        {formatAmount(DashboardData?.bookedPnlAmt)}
                                                    </p>
                                                    {/* <p
                                                        class={`${DashboardData?.bookedPnlAmt > 0 ? "text-success-emphasis" : "text-danger-emphasis"} mb-0 fs-4 dashboard-hero-value`}
                                                    >
                                                        {formatAmount(DashboardData?.bookedPnlAmt)}
                                                    </p> */}
                                                </div>
                                            </div>
                                            <div class="col-md-2 col-12">
                                                <hr class="mb-2 mt-0 d-block d-md-none text-white" />
                                                <div class="text-start">
                                                    <a
                                                        href="#"
                                                        class="text-decoration-none text-muted"
                                                        title="Overall profit or loss combining realized and unrealized returns."
                                                    >
                                                        <small>
                                                            Total P&amp;L{" "}
                                                            <i
                                                                class="fa fa-info-circle"
                                                                aria-hidden="true"
                                                            ></i>
                                                        </small>
                                                    </a>
                                                    <p
                                                        className="mb-0 fs-4 dashboard-hero-value"
                                                        style={{
                                                            color:
                                                                DashboardData?.overallPnl > 0
                                                                    ? "#0AD274"
                                                                    : "#FF4D4F" // choose your red shade
                                                        }}
                                                    // class={`${DashboardData?.overallPnl > 0 ? "text-success-emphasis" : "text-danger-emphasis"} mb-0 fs-4 dashboard-hero-value`}
                                                    >
                                                        {formatAmount(DashboardData?.overallPnl)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row g-3 mt-0 d-none">
                                        <div class="col-md-12">
                                            <h6 class="fw-bold mb-0 mt-2">
                                                <i class="fa fa-bell" aria-hidden="true"></i> Recent
                                                Alerts
                                            </h6>
                                            <a
                                                href="./portfolio-lock.php"
                                                class="py-2 px-3 rounded-2 bg-warning bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-warning rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            Equity
                                                        </small>
                                                    </small>
                                                    <small>
                                                        {" "}
                                                        Book full profit in{" "}
                                                        <span style={{ filter: " blur(4px)" }}>
                                                            xxxx
                                                        </span>{" "}
                                                        CMP{" "}
                                                        <span style={{ filter: " blur(4px)" }}>xxxx</span>
                                                    </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                            <a
                                                href="./multibagger-lock.php"
                                                class="py-2 px-3 rounded-2 bg-primary bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-primary rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            Multibaggers
                                                        </small>
                                                    </small>
                                                    <small> New recommendation </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                            <a
                                                href="./etf-lock.php"
                                                class="py-2 px-3 rounded-2 bg-danger bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-danger rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            ETFs
                                                        </small>
                                                    </small>
                                                    <small>
                                                        {" "}
                                                        Book full profit in{" "}
                                                        <span style={{ filter: " blur(4px)" }}>
                                                            xxxx
                                                        </span>{" "}
                                                        CMP{" "}
                                                        <span style={{ filter: " blur(4px)" }}>xxxx</span>
                                                    </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                            <a
                                                href="./ipo.php"
                                                class="py-2 px-3 rounded-2 border mt-3 d-inline-block text-decoration-none text-dark"
                                            >
                                                <div class="d-flex gap-2 align-items-center">
                                                    <small>
                                                        <small class="bg-secondary rounded-2 text-white px-2 py-0 me-2 py-1">
                                                            IPO
                                                        </small>
                                                    </small>
                                                    <small>
                                                        {" "}
                                                        Kindly Avoid to Subscribe Apollo Techno Industries
                                                        Ltd. IPO{" "}
                                                    </small>{" "}
                                                    <i
                                                        class="ms-3 fa fa-chevron-right fa-xs"
                                                        aria-hidden="true"
                                                    ></i>
                                                </div>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-lg-4 order-1 order-lg-1 dashboard-allocation-column">

                                    <div class="d-flex">
                                        {/* <!-- <i class="fa fa-chart-pie mt-1"></i> --> */}
                                        <div class="">
                                            {/* <!-- <h6 class="fw-bold mb-1 mt-0 mt-3 mt-md-0">Your Risk Profile: <span class="text-success fw-normal">Aggressive</span></h6> --> */}
                                            <h6 class="d-block fw-bold text-capitalize mb-4 mt-2">
                                                Suggested Fund Allocation
                                            </h6>
                                        </div>
                                    </div>
                                    <div class="border rounded-3 p-3 d-none">
                                        {/* <div class="d-flex justify-content-center gap-4 align-items-center"> */}
                                        <PortfolioAllocationChart data={allocationData} />
                                        {/* </div> */}
                                    </div>
                                    {/* <div className=" rounded-2 p-3 dashboard-allocation-card dashboard-hero-allocation-card"> */}
                                    <div
                                        className={`rounded-2 p-3 dashboard-allocation-card dashboard-hero-allocation-card ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                            }`}
                                        style={{
                                            border: theme === "light" ? "1px solid #ddd" : "none",
                                        }}
                                    >
                                        {/* 🔹 Allocation Progress */}
                                        <div className="mb-1">
                                            <div className="d-flex justify-content-between mb-2 dashboard-allocation-split">
                                                <small className="fw-bold">Equity - 60%</small>
                                                <small className="fw-bold">ETF - 40%</small>
                                            </div>

                                            <div className="progress dashboard-allocation-progress" style={{ height: "6px" }}>
                                                <div
                                                    className="progress-bar bg-warning"
                                                    style={{ width: "60%" }}
                                                    role="progressbar"
                                                    aria-label="Recommended equity allocation"
                                                    aria-valuenow="60"
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                />
                                                <div
                                                    className="progress-bar bg-success"
                                                    style={{ width: "40%" }}
                                                    role="progressbar"
                                                    aria-label="Recommended ETF allocation"
                                                    aria-valuenow="40"
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                />
                                            </div>
                                        </div>

                                        <div className="dashboard-allocation-copy mt-3">
                                            <div className="mb-2">
                                                <h6 className="fw-bold d-inline-block mb-0">Multibagger:</h6>
                                                <small
                                                    className={`dashboard-allocation-note ms-1 ${theme === "dark"
                                                        ? "text-white text-opacity-75"
                                                        : "text-muted"
                                                        }`}
                                                >
                                                    Min. 20,000 per stock / month
                                                </small>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold d-inline-block mb-0">Opportunities:</h6>
                                                <small
                                                    className={`dashboard-allocation-note ms-1 ${theme === "dark"
                                                        ? "text-white text-opacity-75"
                                                        : "text-muted"
                                                        }`}
                                                >
                                                    As and when available
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-lg-8 order-2 order-lg-2 dashboard-products-column">
                                    <h6 class="fw-bold my-3">
                                        <i class="fa fa-chart-pie" aria-hidden="true"></i> Financial
                                        Products
                                    </h6>
                                    {/* {(expiredServices.length > 0 || expiringSoonServices.length > 0) && (
                                        <div className="mt-2 rounded rounded-2 alert alert-danger dashboard-expiry-alert">
                                            <div className="d-flex justify-content-between align-items-center dashboard-expiry-alert-header">
                                                <span className="dashboard-expiry-title">Service Expiry Alert</span>

                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light dashboard-expiry-toggle"
                                                    onClick={() => setShowExpiryDetails((isOpen) => !isOpen)}
                                                    aria-expanded={showExpiryDetails}
                                                    aria-controls="dashboard-expiry-collapse"
                                                    aria-label={showExpiryDetails ? "Hide service expiry details" : "Show service expiry details"}
                                                    title={showExpiryDetails ? "Hide details" : "View details"}
                                                >
                                                    <i
                                                        className={`fa fa-chevron-${showExpiryDetails ? "up" : "down"}`}
                                                        aria-hidden="true"
                                                    ></i>
                                                </button>
                                            </div>
                                            <div
                                                id="dashboard-expiry-collapse"
                                                className={`collapse ${showExpiryDetails ? "show" : ""}`}
                                            >
                                                <div className="dashboard-expiry-details mt-3 pt-3 border-top">
                                                    <div className="d-flex flex-column gap-2">
                                                        {expiringSoonServices.map((service, index) => (
                                                            <div
                                                                className="dashboard-expiry-message dashboard-expiry-message-soon"
                                                                key={`expiring-${service?.id || service?.pacName || index}`}
                                                            >
                                                                {getExpiryMessage(service, false)}
                                                            </div>
                                                        ))}

                                                        {expiredServices.map((service, index) => (
                                                            <div
                                                                className="dashboard-expiry-message dashboard-expiry-message-expired"
                                                                key={`expired-${service?.id || service?.pacName || index}`}
                                                            >
                                                                {getExpiryMessage(service, true)}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="btn btn-sm mt-3 dashboard-contact-sales-btn"
                                                        onClick={handleContactSales}
                                                        disabled={isRenewRequestLoading}
                                                    >
                                                        {isRenewRequestLoading ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-2"
                                                                    aria-hidden="true"
                                                                ></span>
                                                                Sending...
                                                            </>
                                                        ) : (
                                                            "Contact Sales"
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )} */}
                                    {hasActiveServices.length == 0 &&
                                        <div className="w-100 col-12 text-body-emphasis text-center rounded rounded-2 alert alert-danger dashboard-expiry-alert">
                                            <div>No Active Service</div>
                                            <button
                                                type="button"
                                                className="btn btn-sm mt-3"
                                                style={primaryButtonStyle}
                                                onClick={handleContactSales}
                                                disabled={isRenewRequestLoading}
                                            >
                                                {isRenewRequestLoading ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            aria-hidden="true"
                                                        ></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    "Contact Sales"
                                                )}
                                            </button>
                                        </div>
                                    }

                                    {/* <div class="row g-3"> */}
                                    <div className="row g-3 dashboard-products-grid">
                                        {DashboardData?.products?.filter((item)=> item.pacId !== 86).map((item, index) => (
                                            <div className="col-sm-6 dashboard-product-col" key={index}>
                                                {/* <div
                                                    className={`p-3 rounded-2 h-100 bg-gradient position-relative dashboard-product-card ${item.invType === "equity"
                                                        ? "bg-warning bg-opacity-25"
                                                        : item.invType === "etf"
                                                            ? "bg-success bg-opacity-25"
                                                            : "bg-primary bg-opacity-25"
                                                        }`}
                                                > */}
                                                <div
                                                    className={`p-3 rounded-2 h-100 bg-gradient position-relative dashboard-product-card ${item.invType === "equity"
                                                        ? "bg-warning bg-opacity-25"
                                                        : item.invType === "etf"
                                                            ? "bg-success bg-opacity-25"
                                                            : "bg-primary bg-opacity-25"
                                                        }`}
                                                    style={{ overflow: "visible" }}
                                                >


                                                    {item.mainMessagesCount > 0 && (
                                                        <span
                                                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                                        // style={{ fontSize: "10px" }}
                                                        >
                                                            {item.mainMessagesCount}
                                                        </span>
                                                    )}
                                                    <a
                                                        onClick={() =>
                                                            handlePortfolioRedirect(item)
                                                        }
                                                        href={
                                                            item.isSubscribed && item.invested == 0
                                                                ? item.invType === "equity"
                                                                    ? "/portfolio-allocation"
                                                                    : item.invType === "etf"
                                                                        ? "/etf-allocation"
                                                                        : "/multibagger"
                                                                : item.isSubscribed
                                                                    ? item.invType === "equity"
                                                                        ? "/portfolio"
                                                                        : item.invType === "etf"
                                                                            ? "/etf"
                                                                            : "/multibagger"
                                                                    : item.invType === "equity"
                                                                        ? "/modalportfolio"
                                                                        : item.invType === "etf"
                                                                            ? "/etf-lock"
                                                                            : "/multibaggers-lock"
                                                        }
                                                        className="text-body-emphasis text-decoration-none"
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start dashboard-product-header">
                                                            <div className="me-3">
                                                                <h1 className="fw-bold fs-4 mb-1 dashboard-product-title">
                                                                    {item.invType === "equity"
                                                                        ? "Equity"
                                                                        : item.invType === "etf"
                                                                            ? "ETF"
                                                                            : "Multibaggers"}
                                                                </h1>

                                                                {/* <small className="text-body-emphasis dashboard-product-copy">
                                                                    {item.invType === "equity"
                                                                        ? "A carefully curated selection of high-potential stocks across sectors"
                                                                        : item.invType === "etf"
                                                                            ? "Diversified exchange-traded funds for stable growth"
                                                                            : "High-growth opportunities with long-term potential"}
                                                                </small> */}
                                                                <small
                                                                    className={`dashboard-product-copy ${theme !== "dark" ? "text-body-emphasis" : "text-white"
                                                                        }`}
                                                                    style={theme === "dark" ? { opacity: 0.75 } : {}}
                                                                >
                                                                    {item.invType === "equity"
                                                                        ? "A carefully curated selection of high-potential stocks across sectors"
                                                                        : item.invType === "etf"
                                                                            ? "Diversified exchange-traded funds for stable growth"
                                                                            : "High-growth opportunities with long-term potential"}
                                                                </small>
                                                            </div>

                                                            <i className="fa fa-arrow-right mt-2" />
                                                        </div>
                                                    </a>

                                                    <div className="d-flex justify-content-between align-items-center mt-md-4 mt-3 dashboard-product-metrics">
                                                        <div>
                                                            <small className="text-muted d-block">
                                                                Invested
                                                            </small>
                                                            <h5 className="mb-0">
                                                                ₹{" "}
                                                                {item.isSubscribed
                                                                    ? item.invested.toLocaleString("en-IN", {
                                                                        maximumFractionDigits: 2,
                                                                    })
                                                                    : 0}
                                                            </h5>
                                                        </div>

                                                        <div className="text-end">
                                                            <small className="text-muted d-block">
                                                                {/* Current P&amp;L */}
                                                                {/* Current value */}
                                                                Overall PnL
                                                            </small>
                                                            <h5
                                                                className="mb-0"
                                                            // className={`mb-0 ${item.currentPnlAmt >= 0
                                                            //     ? "text-success"
                                                            //     : "text-danger"
                                                            //     }`}
                                                            >
                                                                ₹{" "}
                                                                {item.isSubscribed
                                                                    ? (item.currentPnlAmt + item.bookedPnlAmt).toLocaleString("en-IN", {
                                                                        maximumFractionDigits: 2,
                                                                    })
                                                                    : 0}
                                                            </h5>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* <div class="col-md-6">
                                            <div class="bg-warning bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                                <a href={DashboardData?.products[0]?.isSubscribed ? "/portfolio" : "/portfolio-lock"} class="text-body-emphasis text-decoration-none">
                                                    <div class="d-flex justify-content-between align-items-start">
                                                        <div class="me-3">
                                                            <h1 class="fw-bold fs-4 mb-1">Equity</h1>
                                                            <small class="text-black">A carefully curated selection of high-potential stocks across sectors</small>
                                                        </div>
                                                        <i class="fa fa-arrow-right mt-2" aria-hidden="true"></i>
                                                    </div>
                                                </a>
                                                <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                                    <div class="">
                                                        <small class="text-muted d-block">Invested</small>
                                                        <h5 class="mb-0">{DashboardData?.products[0]?.invested.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                    <div class="text-end">
                                                        <small class="text-muted d-block">Current P&amp;L</small>
                                                        <h5 class="mb-0">{DashboardData?.products[0]?.currentVal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                         <div class="col-md-6">
                                            <div class="bg-primary bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                                <a href={DashboardData?.products[2]?.isSubscribed ? "/multibagger" : "/multibagger-lock"} class="text-body-emphasis text-decoration-none">
                                                    <div class="d-flex justify-content-between align-items-start">
                                                        <div class="me-3">
                                                            <h1 class="fw-bold fs-4 mb-1">Multibaggers</h1>
                                                            <small class="text-black">Fundamentally strong stocks with long-term growth potential.</small>
                                                        </div>
                                                        <i class="fa fa-arrow-right mt-2" aria-hidden="true"></i>
                                                    </div>
                                                </a>
                                                <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                                    <div class="">
                                                        <small class="text-muted d-block">Invested</small>
                                                        <h5 class="mb-0">{DashboardData?.products[2]?.invested.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                    <div class="text-end">
                                                        <small class="text-muted d-block">Current P&amp;L</small>
                                                        <h5 class="mb-0">{DashboardData?.products[2]?.currentVal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>

                                        <div class="col-md-6">
                                            <div class="bg-success bg-opacity-25 p-3 rounded-3 h-100 bg-gradient">
                                                <div
                                                    onClick={() => { handleRedirect(DashboardData?.products[1]?.invPortId, 1, "/etf", "/etf-lock") }} class="text-body-emphasis text-decoration-none">
                                                    <div class="d-flex justify-content-between align-items-start">
                                                        <div class="me-3">
                                                            <h1 class="fw-bold fs-4 mb-1">ETFS</h1>
                                                            <small class="text-black">ETFs with low cost, high liquidity and a clear long-term strategy.</small>
                                                        </div>
                                                        <i class="fa fa-arrow-right mt-2" aria-hidden="true"></i>
                                                    </div>
                                                </div>
                                                <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                                    <div class="">
                                                        <small class="text-muted d-block">Invested</small>
                                                        <h5 class="mb-0">{DashboardData?.products[1]?.invested.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                    <div class="text-end">
                                                        <small class="text-muted d-block">Current P&amp;L</small>
                                                        <h5 class="mb-0">{DashboardData?.products[1]?.currentVal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</h5>
                                                    </div>
                                                </div>
                                            </div>
                                        </div> */}

                                        <div class="col-sm-6 dashboard-product-col">
                                            <div class="bg-danger bg-opacity-25 p-3 rounded-2 h-100 bg-gradient dashboard-product-card">
                                                <a
                                                    href={`/live-calls?pacIds=${DashboardData.myServices.find(
                                                        (i) =>
                                                            i.pacFirebaseName ===
                                                            "EP_Short_Term_Opportunities",
                                                    )?.pacId || undefined
                                                        }&name=${encodeURIComponent(
                                                            DashboardData.myServices.find(
                                                                (i) =>
                                                                    i.pacFirebaseName ===
                                                                    "EP_Short_Term_Opportunities",
                                                            )?.pacName || "",
                                                        )}`}
                                                    class="text-body-emphasis text-decoration-none"
                                                >
                                                    <div class="d-flex justify-content-between align-items-start dashboard-product-header">
                                                        <div class="me-3">
                                                            <h1 class="fw-bold fs-4 mb-1 dashboard-product-title">Opportunities</h1>
                                                            {/* <small class="text-body-empahsis dashboard-product-copy">
                                                                Never miss a market opportunity. We'll identify
                                                                them and bring them to you.
                                                            </small> */}
                                                            <small
                                                                className={`dashboard-product-copy ${theme !== "dark" ? "text-body-emphasis" : "text-white"
                                                                    }`}
                                                                style={theme === "dark" ? { opacity: 0.75 } : {}}
                                                            >
                                                                Never miss a market opportunity. We'll identify
                                                                them and bring them to you.
                                                            </small>
                                                        </div>
                                                        <i
                                                            class="fa fa-arrow-right mt-2"
                                                            aria-hidden="true"
                                                        ></i>
                                                    </div>
                                                    {/* <!-- <span class="px-2 bg-dark bg-opacity-10 rounded-2 "><small><i class="text-dark fa fa-unlock fa-xs"></i> Unlock Now</small></span> -->
                                                    <!-- <small class="text-muted">Recommended Allocation: <br /><span class="text-black">2,00,000</span></small> --> */}
                                                </a>
                                                {/* <!-- <div class="d-flex justify-content-between align-items-center mt-md-4 mt-3">
                                                    <div class="">
                                                        <small class="text-muted d-block">Invested</small>
                                                        <h5 class="mb-0">2,00,000</h5>
                                                    </div>
                                                    <div class="text-end">
                                                        <small class="text-muted d-block">Current P&L</small>
                                                        <h5 class="mb-0">+15,000</h5>
                                                    </div>
                                                </div> --> */}
                                            </div>
                                        </div>
                                    </div>
                                    {/* </div> */}
                                </div>

                                <div class="col-lg-4 order-3 order-lg-3 dashboard-service-column">
                                    {DashboardData?.myServices?.length > 0 && (
                                        <>
                                            <h6 class="fw-bold my-3">
                                                <i class="fa fa-bell" aria-hidden="true"></i> My Service
                                            </h6>
                                            {/* ================= Investor & Option Hedging ================= */}
                                            {DashboardData?.myServices?.map((item, idx) => (
                                                <React.Fragment key={idx}>
                                                    {/* Investor */}
                                                    {item.pacFirebaseName === "EP_INVESTORS" && (
                                                        <div
                                                            className={`p-3 mt-2 mb-3 rounded-2 position-relative dashboard-service-card ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                                }`}
                                                            style={{
                                                                border: theme === "light" ? "1px solid #ddd" : "none",
                                                                overflow: "visible",
                                                            }}
                                                        >
                                                            <a
                                                                href={`/live-calls?pacIds=${item.pacId}&name=${encodeURIComponent(item.pacName)}`}
                                                                className="text-body-emphasis text-decoration-none"
                                                            >
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <h6 className="fw-bold mb-1">Investor</h6>
                                                                    <i className="fa fa-arrow-right"></i>
                                                                </div>

                                                                <small
                                                                    className={
                                                                        theme === "dark"
                                                                            ? "text-white text-opacity-75"
                                                                            : "text-muted"
                                                                    }
                                                                >
                                                                    Short-term Investing At Right Time
                                                                </small>
                                                            </a>

                                                            {item.mainMessagesCount > 0 && (
                                                                <span
                                                                    className="position-absolute dashboard-service-badge badge rounded-pill bg-primary"
                                                                    style={{ zIndex: 10 }}
                                                                >
                                                                    {item.mainMessagesCount}
                                                                    <span className="visually-hidden">New</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Option Hedging */}
                                                    {item.pacFirebaseName === "EP_OPTION_HEDGING" && (
                                                        <div
                                                            className={`p-3 my-2 rounded-2 position-relative dashboard-service-card ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                                }`}
                                                            style={{
                                                                border: theme === "light" ? "1px solid #ddd" : "1px solid transparent",
                                                                overflow: "visible",
                                                            }}
                                                        >
                                                            <a
                                                                href={`/live-calls?pacIds=${item.pacId}&name=${encodeURIComponent(item.pacName)}`}
                                                                className="text-body-emphasis text-decoration-none"
                                                            >
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <h6 className="fw-bold mb-1">Option Hedging</h6>
                                                                    <i className="fa fa-arrow-right"></i>
                                                                </div>

                                                                <small className="text-muted">
                                                                    A risk management strategy
                                                                </small>
                                                            </a>

                                                            {item.mainMessagesCount > 0 && (
                                                                <span
                                                                    className="position-absolute dashboard-service-badge badge rounded-pill bg-primary"
                                                                    style={{ zIndex: 10 }}
                                                                >
                                                                    {item.mainMessagesCount}
                                                                    <span className="visually-hidden">New</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ))}

                                            {/* ================= Index / Stock Options (ONLY ONCE) ================= */}
                                            {optionServices.length > 0 && (
                                                // <div className=" p-3 mt-2 mb-3 rounded-2 position-relative dashboard-service-card">
                                                <div
                                                    className={`p-3 mt-2 mb-3 rounded-2 position-relative dashboard-service-card ${theme === "dark" ? "text-white text-opacity-75" : "text-muted"
                                                        }`}
                                                    style={{
                                                        border: theme === "light" ? "1px solid #ddd" : "",
                                                        overflow: "visible",
                                                    }}
                                                >
                                                    <a
                                                        href={`/live-calls?pacIds=${optionServices
                                                            .map((i) => i.pacId)
                                                            .join(",")}&name=${encodeURIComponent(
                                                                optionServices[0]?.pacName || "",
                                                            )}`}
                                                        className="text-body-emphasis text-decoration-none"
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <h6 className="fw-bold mb-1">
                                                                Index / Stock Options
                                                            </h6>
                                                            <i className="fa fa-arrow-right"></i>
                                                        </div>
                                                        <small className={`${theme === "dark"
                                                            ? "text-white text-opacity-75"
                                                            : "text-muted"
                                                            }`}>
                                                            Index / Stock Options Recommendations
                                                        </small>
                                                    </a>

                                                    {optionMessagesCount > 0 && (
                                                        <span
                                                            className="position-absolute dashboard-service-badge badge rounded-pill bg-primary"
                                                            style={{ zIndex: 10 }}
                                                        >
                                                            {optionMessagesCount}
                                                            <span className="visually-hidden">New</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {AlertData?.length > 0 && (
                                        <>
                                            <h6 class="fw-bold my-3 dashboard-alerts-title">
                                                <i class="fa fa-bell" aria-hidden="true"></i> Recent
                                                Alerts
                                            </h6>
                                            <div class="border rounded-4 p-3 mb-3 dashboard-alerts-panel">
                                                {AlertData?.map((item, idx) => (
                                                    <React.Fragment key={idx}>
                                                        {item.type === "" && (
                                                            <div class="py-2 px-3 rounded-3 mb-3 dashboard-alert-item">
                                                                <div class="d-flex gap-2 align-items-center dashboard-alert-row">
                                                                    <small class="dashboard-alert-message">
                                                                        {item.serMsg}
                                                                    </small>
                                                                    <i
                                                                        class="ms-auto fa fa-chevron-right fa-xs dashboard-alert-arrow"
                                                                        aria-hidden="true"
                                                                    ></i>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.type === "BUY" && (
                                                            <div class="py-2 px-3 rounded-3 mb-3 dashboard-alert-item">
                                                                <div class="d-flex gap-2 align-items-center dashboard-alert-row">
                                                                    <small class="dashboard-alert-badge">Buy</small>
                                                                    <small class="dashboard-alert-message">
                                                                        {item.serMsg}
                                                                    </small>
                                                                    <i
                                                                        class="ms-auto fa fa-chevron-right fa-xs dashboard-alert-arrow"
                                                                        aria-hidden="true"
                                                                    ></i>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.type === "SELL" && (
                                                            <div class="py-2 px-3 rounded-3 mb-3 dashboard-alert-item">
                                                                <div class="d-flex gap-2 align-items-center dashboard-alert-row">
                                                                    <small class="dashboard-alert-badge dashboard-alert-badge-sell">
                                                                        Sell
                                                                    </small>
                                                                    <small class="dashboard-alert-message">
                                                                        {item.serMsg}
                                                                    </small>
                                                                    <i
                                                                        class="ms-auto fa fa-chevron-right fa-xs dashboard-alert-arrow"
                                                                        aria-hidden="true"
                                                                    ></i>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                                {/* <a href="/portfolio" class="py-2 px-3 rounded-2 bg-warning bg-opacity-10 d-inline-block text-decoration-none text-dark">
                                                    <div class="d-flex gap-2 align-items-center">
                                                        <small><small class="bg-warning rounded-2 text-white px-2 py-0 me-2 py-1">Equity</small></small>
                                                        <small> Book full profit in <span style={{ "filter": " blur(4px)" }}>xxxx</span> CMP <span style={{ "filter": " blur(4px)" }}>xxxx</span></small> <i class="ms-3 fa fa-chevron-right fa-xs" aria-hidden="true"></i>
                                                    </div>
                                                </a>
                                                <a href="/multibagger" class="py-2 px-3 rounded-2 bg-primary bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark">
                                                    <div class="d-flex gap-2 align-items-center">
                                                        <small><small class="bg-primary rounded-2 text-white px-2 py-0 me-2 py-1">Multibaggers</small></small>
                                                        <small> New recommendation </small> <i class="ms-3 fa fa-chevron-right fa-xs" aria-hidden="true"></i>
                                                    </div>
                                                </a>
                                                <a href="/etf" class="py-2 px-3 rounded-2 bg-danger bg-opacity-10 mt-3 d-inline-block text-decoration-none text-dark">
                                                    <div class="d-flex gap-2 align-items-center">
                                                        <small><small class="bg-danger rounded-2 text-white px-2 py-0 me-2 py-1">ETFs</small></small>
                                                        <small> Book full profit in <span style={{ "filter": "blur(4px)" }}>xxxx</span> CMP <span style={{ "filter": "blur(4px)" }}>xxxx</span></small> <i class="ms-3 fa fa-chevron-right fa-xs" aria-hidden="true"></i>
                                                    </div>
                                                </a>
                                                <a href="/ipo" class="py-2 px-3 rounded-2 bg-secondary bg-opacity-10 border mt-3 d-inline-block text-decoration-none text-dark">
                                                    <div class="d-flex gap-2 align-items-center">
                                                        <small><small class="bg-secondary rounded-2 text-white px-2 py-0 me-2 py-1">IPO</small></small>
                                                        <small> Kindly Avoid to Subscribe Apollo Techno Industries Ltd. IPO </small> <i class="ms-3 fa fa-chevron-right fa-xs" aria-hidden="true"></i>
                                                    </div>
                                                </a> */}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )
            }
        </Layout >
    );
}
